/**
 * Zustand store for Creative Studio state management.
 *
 * Manages:
 * - Generation form state (prompt, settings)
 * - Current job/session tracking
 * - UI state (active tab, loading states)
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { REFERENCE_IMAGE_NAME_MAX_LENGTH } from '@/types/creativeStudio';
import type {
  AspectRatio,
  ImageResolution,
  GeneratedImage,
  RefinementSessionResponse,
  GenerationResultResponse,
} from '@/types/creativeStudio';

/**
 * A reference image as tracked by the store: the Media Library UUID plus a
 * user-facing display name (max 12 chars). Mirrors the backend's
 * ReferenceImageInfo shape, minus the resolved signed URL (which is fetched
 * separately via useMediaImage when rendering thumbnails).
 */
export interface NamedReferenceImage {
  id: string;
  name: string;
}

/**
 * Return the next free `ref-image-N` slot given a list of existing names.
 * Starts at 1; finds the smallest positive integer N where `ref-image-N`
 * is not already used. Also treats the legacy `reference-image-N` and
 * `ref-image N` slots as used so a session created before the rename-deploy
 * doesn't collide. The cap is not enforced here because the caller already
 * caps the ref list at 14 entries, well below the name-length budget.
 */
export function nextDefaultRefImageName(existingNames: string[]): string {
  const used = new Set(existingNames);
  let n = 1;
  while (
    used.has(`ref-image-${n}`) ||
    used.has(`reference-image-${n}`) ||
    used.has(`ref-image ${n}`)
  ) {
    n += 1;
  }
  return `ref-image-${n}`;
}

/**
 * Normalize a user-supplied name (collapse CR/LF to single space, trim, cap
 * to REFERENCE_IMAGE_NAME_MAX_LENGTH). Returns null if the result is empty
 * so callers can substitute a default.
 *
 * CR/LF runs are collapsed to a single space rather than removed so that
 * "logo\nbrand" becomes "logo brand" instead of "logobrand". The backend
 * rejects CR/LF entirely; sanitizing here avoids surprise 422s when a non-FE
 * caller sets an explicit name. The React UI no longer surfaces a naming
 * step — names are auto-assigned by `nextDefaultRefImageName`.
 */
export function normalizeRefImageName(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const cleaned = raw.replace(/[\r\n]+/g, ' ').trim();
  if (!cleaned) return null;
  return cleaned.slice(0, REFERENCE_IMAGE_NAME_MAX_LENGTH);
}

// =============================================================================
// Types
// =============================================================================

type ActiveTab = 'generate' | 'edit';

interface CreativeStudioState {
  // Generation form state
  prompt: string;
  aspectRatio: AspectRatio;
  resolution: ImageResolution;
  referenceImages: NamedReferenceImage[];
  enableGrounding: boolean;

  // Current job
  currentJobId: string | null;
  currentJobResult: GenerationResultResponse | null;
  generatedImages: GeneratedImage[];

  // Refinement session state
  refinementSessionId: string | null;
  refinementSession: RefinementSessionResponse | null;
  isRefining: boolean;

  // UI state
  isGenerating: boolean;
  activeTab: ActiveTab;
  selectedImageIndex: number;
  showReferenceImagePicker: boolean;
  showSaveModal: boolean;
}

interface CreativeStudioActions {
  // Form actions
  setPrompt: (prompt: string) => void;
  setAspectRatio: (ratio: AspectRatio) => void;
  setResolution: (resolution: ImageResolution) => void;
  setReferenceImages: (images: NamedReferenceImage[]) => void;
  addReferenceImage: (id: string, name?: string | null) => void;
  removeReferenceImage: (id: string) => void;
  renameReferenceImage: (id: string, name: string) => void;
  setEnableGrounding: (enabled: boolean) => void;

  // Job actions
  setCurrentJobId: (id: string | null) => void;
  setCurrentJobResult: (result: GenerationResultResponse | null) => void;
  setGeneratedImages: (images: GeneratedImage[]) => void;

  // Refinement session actions
  setRefinementSession: (session: RefinementSessionResponse | null) => void;
  setIsRefining: (refining: boolean) => void;

  // UI actions
  setIsGenerating: (generating: boolean) => void;
  setActiveTab: (tab: ActiveTab) => void;
  selectImage: (index: number) => void;
  setShowReferenceImagePicker: (show: boolean) => void;
  setShowSaveModal: (show: boolean) => void;

  // Compound actions
  startGeneration: (jobId: string) => void;
  completeGeneration: (result: GenerationResultResponse) => void;
  failGeneration: (error: string) => void;

  // Refinement compound actions
  startRefinementSession: (session: RefinementSessionResponse) => void;
  updateRefinementSession: (session: RefinementSessionResponse) => void;
  endRefinementSession: () => void;

  reset: () => void;
  resetForm: () => void;
}

type CreativeStudioStore = CreativeStudioState & CreativeStudioActions;

// =============================================================================
// Initial State
// =============================================================================

const initialState: CreativeStudioState = {
  // Form state
  prompt: '',
  aspectRatio: '1:1',
  resolution: '1K',
  referenceImages: [],
  enableGrounding: false,

  // Job state
  currentJobId: null,
  currentJobResult: null,
  generatedImages: [],

  // Refinement session state
  refinementSessionId: null,
  refinementSession: null,
  isRefining: false,

  // UI state
  isGenerating: false,
  activeTab: 'generate',
  selectedImageIndex: 0,
  showReferenceImagePicker: false,
  showSaveModal: false,
};

// =============================================================================
// Store
// =============================================================================

export const useCreativeStudioStore = create<CreativeStudioStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Form actions
      setPrompt: (prompt) => set({ prompt }),
      setAspectRatio: (aspectRatio) => set({ aspectRatio }),
      setResolution: (resolution) => set({ resolution }),
      setReferenceImages: (referenceImages) =>
        // Hard-cap at 14 here so the limit can't be bypassed via a bulk
        // setter (companion to the same check in addReferenceImage).
        set({ referenceImages: referenceImages.slice(0, 14) }),
      addReferenceImage: (id, name) => {
        const { referenceImages } = get();
        if (referenceImages.some((r) => r.id === id) || referenceImages.length >= 14) {
          return;
        }
        const normalized = normalizeRefImageName(name);
        // Refuse a user-supplied name that another ref already uses — the
        // backend rejects duplicates because the refinement prompt
        // enumerates by name. Defaults skip used slots so they can't
        // collide here. Callers should re-check before invoking so they
        // can surface a toast (silent no-op is defense-in-depth).
        if (normalized && referenceImages.some((r) => r.name === normalized)) {
          return;
        }
        const resolvedName =
          normalized ?? nextDefaultRefImageName(referenceImages.map((r) => r.name));
        set({
          referenceImages: [...referenceImages, { id, name: resolvedName }],
        });
      },
      removeReferenceImage: (id) => {
        const { referenceImages } = get();
        set({ referenceImages: referenceImages.filter((r) => r.id !== id) });
      },
      renameReferenceImage: (id, name) => {
        const { referenceImages } = get();
        const normalized = normalizeRefImageName(name);
        if (!normalized) return;
        // Refuse to set a name another ref already uses — the backend
        // rejects duplicates (the refinement prompt enumerates by name),
        // so silently letting the UI drift would just defer the failure
        // to the next /generate call. Callers can re-check before calling
        // to surface a toast to the user.
        if (referenceImages.some((r) => r.id !== id && r.name === normalized)) {
          return;
        }
        set({
          referenceImages: referenceImages.map((r) =>
            r.id === id ? { ...r, name: normalized } : r
          ),
        });
      },
      setEnableGrounding: (enableGrounding) => set({ enableGrounding }),

      // Job actions
      setCurrentJobId: (currentJobId) => set({ currentJobId }),
      setCurrentJobResult: (currentJobResult) => set({ currentJobResult }),
      setGeneratedImages: (generatedImages) => set({ generatedImages }),

      // Refinement session actions
      setRefinementSession: (refinementSession) =>
        set({
          refinementSession,
          refinementSessionId: refinementSession?.session_id || null,
        }),
      setIsRefining: (isRefining) => set({ isRefining }),

      // UI actions
      setIsGenerating: (isGenerating) => set({ isGenerating }),
      setActiveTab: (activeTab) => set({ activeTab }),
      selectImage: (selectedImageIndex) => set({ selectedImageIndex }),
      setShowReferenceImagePicker: (showReferenceImagePicker) => set({ showReferenceImagePicker }),
      setShowSaveModal: (showSaveModal) => set({ showSaveModal }),

      // Compound actions
      startGeneration: (jobId) => {
        set({
          currentJobId: jobId,
          currentJobResult: null,
          isGenerating: true,
          generatedImages: [],
          selectedImageIndex: 0,
        });
      },

      completeGeneration: (result) =>
        set({
          currentJobResult: result,
          isGenerating: false,
          generatedImages: result.images || [],
          selectedImageIndex: 0,
        }),

      failGeneration: (error) =>
        set({
          currentJobResult: {
            job_id: get().currentJobId || '',
            status: 'failed',
            error_message: error,
            created_at: new Date().toISOString(),
          },
          isGenerating: false,
        }),

      // Refinement compound actions
      startRefinementSession: (session) =>
        set({
          refinementSessionId: session.session_id,
          refinementSession: session,
          isRefining: false,
          activeTab: 'edit',
        }),

      updateRefinementSession: (session) =>
        set({
          refinementSession: session,
          isRefining: false,
        }),

      endRefinementSession: () =>
        set({
          refinementSessionId: null,
          refinementSession: null,
          isRefining: false,
          activeTab: 'generate',
        }),

      reset: () => set(initialState),

      resetForm: () =>
        set({
          prompt: '',
          referenceImages: [],
          enableGrounding: false,
        }),
    }),
    { name: 'creative-studio-store' }
  )
);

// =============================================================================
// Selectors
// =============================================================================

/**
 * Get the currently selected image (from generation or refinement).
 */
export function useSelectedImage(): GeneratedImage | null {
  const generatedImages = useCreativeStudioStore((s) => s.generatedImages);
  const refinementSession = useCreativeStudioStore((s) => s.refinementSession);
  const selectedImageIndex = useCreativeStudioStore((s) => s.selectedImageIndex);
  const activeTab = useCreativeStudioStore((s) => s.activeTab);

  if (activeTab === 'edit' && refinementSession) {
    return refinementSession.current_image;
  }

  if (generatedImages.length > 0 && selectedImageIndex < generatedImages.length) {
    return generatedImages[selectedImageIndex];
  }

  return null;
}

/**
 * Check if there's an active generation or refinement session.
 */
export function useHasActiveWork(): boolean {
  const isGenerating = useCreativeStudioStore((s) => s.isGenerating);
  const refinementSessionId = useCreativeStudioStore((s) => s.refinementSessionId);
  const generatedImages = useCreativeStudioStore((s) => s.generatedImages);

  return (
    isGenerating ||
    refinementSessionId !== null ||
    generatedImages.length > 0
  );
}

/**
 * Get refinement session info if active.
 */
export function useRefinementInfo() {
  const refinementSession = useCreativeStudioStore((s) => s.refinementSession);
  const isRefining = useCreativeStudioStore((s) => s.isRefining);

  return {
    isActive: refinementSession !== null,
    session: refinementSession,
    canRefine: refinementSession?.can_refine ?? false,
    refinementCount: refinementSession?.refinement_count ?? 0,
    maxRefinements: refinementSession?.max_refinements ?? 10,
    originalPrompt: refinementSession?.original_prompt ?? '',
    isRefining,
  };
}

/**
 * Get generation form values.
 */
export function useGenerationForm() {
  const prompt = useCreativeStudioStore((s) => s.prompt);
  const aspectRatio = useCreativeStudioStore((s) => s.aspectRatio);
  const resolution = useCreativeStudioStore((s) => s.resolution);
  const referenceImages = useCreativeStudioStore((s) => s.referenceImages);
  const enableGrounding = useCreativeStudioStore((s) => s.enableGrounding);

  return {
    prompt,
    aspectRatio,
    resolution,
    referenceImages,
    enableGrounding,
  };
}
